import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FuriganaText from './FuriganaText';

// Phase A (burden-probe flag only): the task-level one-item effort question,
// asked after some answers (the server decides when). Five faces plus "skip".
// Answering is optional and changes nothing else on screen. The thank-you shows only
// after the save is confirmed; a failed save keeps the faces and asks again (the
// parent resends with the same event id, so one prompt yields at most one rating).
const FACES = ['😀', '🙂', '😐', '😟', '😣'];

function BurdenProbe({ onAnswer }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState('idle'); // idle | saving | done | error
  const mountedRef = useRef(true);
  // Set in every setup: StrictMode runs setup -> cleanup -> setup on mount (Codex B-R5).
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const answer = async (rating) => {
    if (status === 'saving' || status === 'done') return;
    setStatus('saving');
    let ok = false;
    try {
      ok = (await onAnswer?.(rating)) !== false;
    } catch (_) {
      ok = false;
    }
    if (mountedRef.current) setStatus(ok ? 'done' : 'error');
  };
  const saving = status === 'saving';

  if (status === 'done') {
    return (
      <div role="status" className="rounded-2xl border p-3 text-sm" style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
        <FuriganaText text={t('research.burdenThanks')} />
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label={t('research.burdenQuestion')}
      data-research-burden-probe="true"
      className="rounded-2xl border p-3"
      style={{ borderColor: 'var(--primary)', backgroundColor: 'var(--bg-surface)' }}
    >
      <div className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
        <FuriganaText text={t('research.burdenQuestion')} />
      </div>
      <div className="mt-2 flex flex-wrap items-stretch gap-2">
        {FACES.map((face, index) => {
          const n = index + 1;
          const label = t(`research.burdenLevel${n}`);
          return (
            <button
              key={n}
              type="button"
              onClick={() => answer(n)}
              disabled={saving}
              aria-label={t('research.burdenAria', { n, label })}
              className="flex min-w-[4.5rem] flex-col items-center rounded-xl border px-2 py-1"
              style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-muted)', color: 'var(--text)' }}
            >
              <span className="text-2xl" aria-hidden="true">{face}</span>
              <span className="text-xs"><FuriganaText text={label} /></span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => answer(null)}
          disabled={saving}
          className="self-center rounded-full border px-3 py-1 text-xs"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
        >
          <FuriganaText text={t('research.burdenSkip')} />
        </button>
      </div>
      {status === 'error' && (
        <div role="status" className="mt-2 text-xs" style={{ color: 'var(--danger)' }}>
          <FuriganaText text={t('research.burdenError')} />
        </div>
      )}
    </div>
  );
}

export default BurdenProbe;
